import { useState } from 'react';
import { Input, RadioGroup, RadioGroupItem, Checkbox, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { PasswordInput } from '@vnetwork/ui/shells';
import type { HlsProtection, DashProtection, OutputType, ConfigVerimatrix } from '@/api/media/live-event-jobs-types';
import { FormSectionCard } from './FormSectionCard';
import styles from './EventJobCreate.module.css';

// Top-level HLS mode as shown to the user — "aes" covers both sub-modes
type HlsTopMode = 'none' | 'aes' | 'fairplay' | 'verimatrix';
type AesSubMode = 'aes_default_key_server' | 'aes_third_party_key_server';

const TOP_MODES: { value: HlsTopMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'aes', label: 'AES Encryption' },
  { value: 'fairplay', label: 'FairPlay' },
];

function getTopMode(p: HlsProtection): HlsTopMode {
  if (p.mode === 'verimatrix') return 'verimatrix';
  if (p.mode === 'fairplay') return 'fairplay';
  if (p.mode === 'aes_default_key_server' || p.mode === 'aes_third_party_key_server') return 'aes';
  return 'none';
}

function getAesSub(p: HlsProtection): AesSubMode {
  return p.mode === 'aes_third_party_key_server' ? 'aes_third_party_key_server' : 'aes_default_key_server';
}

/** Derive resource_id from a verimatrix config entry (legacy priority: resource_id → resourceId → cp_id) */
const getVerimatrixResourceId = (item?: ConfigVerimatrix): string =>
  String(item?.resource_id ?? item?.resourceId ?? item?.cp_id ?? '');

interface ProtectionSectionProps {
  outputs: OutputType[];
  hlsProtection: HlsProtection;
  dashProtection: DashProtection;
  /** Replace the whole HlsProtection object (mode change must always replace, not patch). */
  onHls: (next: HlsProtection) => void;
  /** Replace the whole DashProtection object. */
  onDash: (next: DashProtection) => void;
  /** Verimatrix DRM entries from service config (optional — linear wizard passes these). */
  verimatrixes?: ConfigVerimatrix[] | null;
  /** Per-field validation errors keyed by field path (e.g. "hls.resource_id"). */
  errors?: Record<string, string>;
}

/** HLS encryption + DASH DRM options. Only rendered when hls/dash are in outputs. */
export function ProtectionSection({ outputs, hlsProtection, dashProtection, onHls, onDash, verimatrixes, errors = {} }: ProtectionSectionProps) {
  // ALL hooks must appear before any early return — moving state here prevents hook-count crash
  // when HLS/DASH outputs toggle (React rule: hooks must be called unconditionally).
  const [aesSub, setAesSub] = useState<AesSubMode>(getAesSub(hlsProtection));

  const hasHls = outputs.includes('hls');
  const hasDash = outputs.includes('dash');
  if (!hasHls && !hasDash) return null;

  const verimatrixOptions = verimatrixes ?? [];
  const topMode = getTopMode(hlsProtection);

  // Replace whole HlsProtection object on every top-mode change (not patch-merge)
  const handleTopModeChange = (next: HlsTopMode) => {
    if (next === 'none') {
      onHls({ mode: 'none' });
    } else if (next === 'fairplay') {
      onHls({ mode: 'fairplay' });
    } else if (next === 'verimatrix') {
      const selected = verimatrixOptions[0];
      onHls({
        mode: 'verimatrix',
        verimatrix_id: selected ? String(selected.id) : '',
        resource_id: getVerimatrixResourceId(selected),
      });
    } else {
      // AES — restore persisted sub-mode with clean object
      onHls({ mode: aesSub });
    }
  };

  const handleAesSubChange = (sub: AesSubMode) => {
    setAesSub(sub);
    // Replace object preserving existing resource_id / key fields that apply to the sub-mode
    if (sub === 'aes_default_key_server') {
      onHls({ mode: sub, resource_id: hlsProtection.resource_id ?? '' });
    } else {
      onHls({
        mode: sub,
        third_party_key: hlsProtection.third_party_key ?? '',
        key_url_in_playlist: hlsProtection.key_url_in_playlist ?? '',
      });
    }
  };

  const handleVerimatrixChange = (id: string) => {
    const selected = verimatrixOptions.find((item) => String(item.id) === id);
    onHls({
      mode: 'verimatrix',
      verimatrix_id: id,
      resource_id: getVerimatrixResourceId(selected),
    });
  };

  const isWidevineEnabled = dashProtection.widevine?.enabled || false;
  const isPlayReadyEnabled = dashProtection.playready?.enabled || false;
  const isNoneEnabled = !isWidevineEnabled && !isPlayReadyEnabled;

  const handleDashCheckboxChange = (type: 'widevine' | 'playready' | 'none', checked: boolean) => {
    if (type === 'none' && checked) {
      onDash({ mode: 'no_drm', widevine: undefined, playready: undefined });
      return;
    }
    const nextWidevine = type === 'widevine' ? checked : type === 'none' ? false : isWidevineEnabled;
    const nextPlayReady = type === 'playready' ? checked : type === 'none' ? false : isPlayReadyEnabled;
    onDash({
      mode: nextWidevine || nextPlayReady ? 'drm' : 'no_drm',
      widevine: nextWidevine ? { enabled: true, content_id: dashProtection.widevine?.content_id || '' } : undefined,
      playready: nextPlayReady ? { enabled: true, content_id: dashProtection.playready?.content_id || '' } : undefined,
    });
  };

  // All modes that can appear including conditional verimatrix
  const allTopModes = verimatrixOptions.length > 0
    ? [...TOP_MODES, { value: 'verimatrix' as HlsTopMode, label: 'Verimatrix DRM' }]
    : TOP_MODES;

  return (
    <FormSectionCard title="Content Protection" description="HLS encryption and DASH DRM settings.">
      {hasHls && (
        <div className={styles.protectionBlock}>
          <p className={styles.subLabel}>HLS Protection</p>
          <fieldset className={styles.fieldsetReset} aria-required="false">
            <legend className={styles.fieldLegend}>Mode</legend>
            {/* RadioGroup replaces checkbox-acting-as-radio for correct semantics */}
            <RadioGroup
              value={topMode}
              onValueChange={(v) => handleTopModeChange(v as HlsTopMode)}
              className={styles.radioGroup}
            >
              {allTopModes.map(({ value, label }) => (
                <label key={value} className={styles.radioItem}>
                  <RadioGroupItem value={value} id={`hls-mode-${value}`} />
                  <span>{label}</span>
                </label>
              ))}
            </RadioGroup>
          </fieldset>

          {/* AES sub-mode selector + fields */}
          {topMode === 'aes' && (
            <>
              <Field label="AES Key Server" required>
                {() => (
                  <RadioGroup
                    value={aesSub}
                    onValueChange={(v) => handleAesSubChange(v as AesSubMode)}
                    className={styles.radioGroup}
                  >
                    <label className={styles.radioItem}>
                      <RadioGroupItem value="aes_default_key_server" id="aes-default" />
                      <span>Default Key Server</span>
                    </label>
                    <label className={styles.radioItem}>
                      <RadioGroupItem value="aes_third_party_key_server" id="aes-third-party" />
                      <span>Third-Party Key Server</span>
                    </label>
                  </RadioGroup>
                )}
              </Field>
              {aesSub === 'aes_default_key_server' && (
                <Field label="Resource ID" required error={errors['hls.resource_id']}>
                  {(fp) => (
                    <Input
                      {...fp}
                      value={hlsProtection.resource_id ?? ''}
                      onChange={(e) => onHls({ ...hlsProtection, resource_id: e.target.value })}
                      placeholder="e.g. resource-id-abc123"
                    />
                  )}
                </Field>
              )}
              {aesSub === 'aes_third_party_key_server' && (
                <>
                  <Field label="Third-Party Key" required error={errors['hls.third_party_key']}>
                    {(fp) => (
                      <PasswordInput
                        {...fp}
                        value={hlsProtection.third_party_key ?? ''}
                        onChange={(e) => onHls({ ...hlsProtection, third_party_key: e.target.value })}
                        placeholder="e.g. my-secret-key"
                      />
                    )}
                  </Field>
                  <Field label="Key URL in Playlist" required error={errors['hls.key_url_in_playlist']}>
                    {(fp) => (
                      <Input
                        {...fp}
                        value={hlsProtection.key_url_in_playlist ?? ''}
                        onChange={(e) => onHls({ ...hlsProtection, key_url_in_playlist: e.target.value })}
                        placeholder="https://keyserver.example.com/key"
                      />
                    )}
                  </Field>
                </>
              )}
            </>
          )}

          {/* FairPlay resource ID */}
          {topMode === 'fairplay' && (
            <Field label="Resource ID" required error={errors['hls.resource_id']}>
              {(fp) => (
                <Input
                  {...fp}
                  value={hlsProtection.resource_id ?? ''}
                  onChange={(e) => onHls({ ...hlsProtection, resource_id: e.target.value })}
                  placeholder="e.g. resource-id-abc123"
                />
              )}
            </Field>
          )}

          {/* Verimatrix entry selector + resource_id */}
          {topMode === 'verimatrix' && verimatrixOptions.length > 0 && (
            <>
              <Field label="Verimatrix DRM" required error={errors['hls.verimatrix_id']}>
                {(fp) => (
                  <Select
                    value={hlsProtection.verimatrix_id ?? ''}
                    onValueChange={handleVerimatrixChange}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue placeholder="Select Verimatrix entry" />
                    </SelectTrigger>
                    <SelectContent>
                      {verimatrixOptions.map((vm) => (
                        <SelectItem key={String(vm.id)} value={String(vm.id)}>
                          {vm.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Resource ID" required error={errors['hls.resource_id']}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={hlsProtection.resource_id ?? ''}
                    onChange={(e) => onHls({ ...hlsProtection, resource_id: e.target.value })}
                    placeholder="e.g. 313"
                  />
                )}
              </Field>
            </>
          )}
        </div>
      )}

      {hasDash && (
        <div className={styles.protectionBlock}>
          <p className={styles.subLabel}>DASH DRM</p>
          <div className={styles.grid2}>
            <div>
              <label className={styles.checkItem}>
                <Checkbox
                  checked={isNoneEnabled}
                  onCheckedChange={(checked) => handleDashCheckboxChange('none', checked === true)}
                />
                <span>None</span>
              </label>
            </div>
            <div>
              <label className={styles.checkItem}>
                <Checkbox
                  checked={isWidevineEnabled}
                  onCheckedChange={(checked) => handleDashCheckboxChange('widevine', checked === true)}
                />
                <span>Widevine Streaming</span>
              </label>
              {isWidevineEnabled && (
                <Field label="Widevine Content ID" required error={errors['dash.widevine.content_id']}>
                  {(fp) => (
                    <Input
                      {...fp}
                      value={dashProtection.widevine!.content_id}
                      onChange={(e) =>
                        onDash({ ...dashProtection, widevine: { enabled: true, content_id: e.target.value } })
                      }
                      placeholder="widevine-content-id"
                    />
                  )}
                </Field>
              )}
            </div>
            <div>
              <label className={styles.checkItem}>
                <Checkbox
                  checked={isPlayReadyEnabled}
                  onCheckedChange={(checked) => handleDashCheckboxChange('playready', checked === true)}
                />
                <span>PlayReady Streaming</span>
              </label>
              {isPlayReadyEnabled && (
                <Field label="PlayReady Content ID" required error={errors['dash.playready.content_id']}>
                  {(fp) => (
                    <Input
                      {...fp}
                      value={dashProtection.playready!.content_id}
                      onChange={(e) =>
                        onDash({ ...dashProtection, playready: { enabled: true, content_id: e.target.value } })
                      }
                      placeholder="playready-content-id"
                    />
                  )}
                </Field>
              )}
            </div>
          </div>
        </div>
      )}
    </FormSectionCard>
  );
}
